/*
  The site footer (§3.4, D14).

  **Restyled after the PCG storefront footer (Robert, 2026-10-20: "similar to the PCG, where
  it links all the offices, all the socials, and just make it a bit more modern … in line
  with the new theme").** It was a flat brand-gold band with three centred link rows. It is
  now the storefront's dark chrome with gold accents, in three columns:

    - the PCG mark, what this site is, and the visitor's nearest PCG office (D22, below);
    - every PCG office: its name links to its grading site, its Facebook / Instagram /
      TikTok sit right beside it (lib/offices officeLinks, below);
    - the policies.

  with the copyright on a bar of its own underneath.

  **One offices list, not two (Robert, 2026-10-21: "combine these two. So instead of having
  double up … Then if you can combine the titles into one. and layout the left section so
  it is spread out").** "Grading sites" and "Follow our offices" listed most offices twice,
  once as a link and once beside its icons. They are now one row per office under one
  heading, with a hint line saying both the name and the icons can be clicked. On xl the
  row is 12 columns: brand 4, offices 6, policies 2, so the offices get the room for two
  columns and the brand column is not squeezed. On lg that split leaves policies ~110px
  wide, so there the brand takes a row of its own and offices and policies share the next,
  9 and 3.

  **Fixed dark in both themes, like the AppBar.** It is chrome, so it does not flip: the
  ground is the storefront header's #0B0E14, the same literal the event skin gives the
  AppBar (src/skins/event.css), and the colours on it are literals too. `text-white` would
  be wrong here: Tailwind's `white` points at --pcg-fg (tailwind.config.js), which is
  near-black on paper, so white text is written `#ffffff`. Measured on #0B0E14: #ffffff at
  72% is 9.5:1, brand gold #DEBB68 is 9.0:1.

  **Width is the page's** (PAGE_CONTAINER), so its columns line up with the content above.
*/

import type { ChangeEvent } from "react";

import LogoImage from "images/pcg-logo.png";
import { PAGE_CONTAINER } from "components/styles";
import { chooseOffice, useOfficeChoice } from "components/useOfficeChoice";
import { GRADING_SITES, OfficeLink, Social, officeByHref, officeLinks } from "lib/offices";

/*
  The ring that belongs on this ground: brand gold, offset by the footer's own colour so it
  reads as a ring rather than a thicker underline. 9:1 against #0B0E14, past WCAG 1.4.11.
*/
const FOOTER_FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#DEBB68] " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0E14]";

const FOOTER_LINK =
  `rounded-sm text-[#ffffff]/[0.72] transition-colors hover:text-[#DEBB68] ${FOOTER_FOCUS_RING}`;

/* The storefront's gold uppercase column heading. */
const FOOTER_HEADING_TEXT = "text-xs font-bold uppercase tracking-[0.16em] text-[#DEBB68]";
const FOOTER_HEADING = `mb-4 ${FOOTER_HEADING_TEXT}`;

/* The gold outline button the old "Visit premiercardgrading.com" link wore. */
const FOOTER_GOLD_BUTTON =
  `inline-flex items-center gap-2 rounded-md border border-[#DEBB68]/60 px-4 py-2 text-sm font-semibold text-[#DEBB68] transition-colors hover:bg-[#DEBB68] hover:text-[#0B0E14] ${FOOTER_FOCUS_RING}`;

/*
  The office select, in the button's gold border on the footer's own ground. Its colours
  are literals for the same reason as the rest of the footer, and `color-scheme: dark`
  plus the options' own colours keep the opened list dark too: left alone, Windows draws it
  white, and white text on it would vanish.
*/
const FOOTER_SELECT =
  `block w-full max-w-xs cursor-pointer rounded-md border border-[#DEBB68]/60 bg-[#0B0E14] px-3 py-2 text-sm text-[#ffffff] [color-scheme:dark] transition-colors hover:border-[#DEBB68] ${FOOTER_FOCUS_RING}`;

const FOOTER_OPTION = "bg-[#0B0E14] text-[#ffffff]";

const SOCIAL_LABEL: Record<Social, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
};

/*
  The office name, as a link to its site. Same tab, as the old "Grading sites" links were:
  it is PCG's own shop, not somewhere else. The arrow and the underline on hover are what
  say "this goes somewhere" (Robert: "indicate that they can click on title or icons").
*/
const OFFICE_NAME_LINK =
  `group inline-flex items-center gap-1 rounded-sm font-medium text-[#ffffff]/[0.85] underline-offset-4 transition-colors hover:text-[#DEBB68] hover:underline ${FOOTER_FOCUS_RING}`;

const SOCIAL_BUTTON =
  `inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#ffffff]/[0.14] text-[#ffffff]/[0.72] transition-colors hover:border-[#DEBB68] hover:text-[#DEBB68] ${FOOTER_FOCUS_RING}`;

/* The storefront's own monochrome icons (snippets/pcg-social-icon.liquid). */
const SocialIcon = ({ icon }: { icon: Social }) => {
  if (icon === "instagram") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2}>
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="h-4 w-4">
      <path
        fill="currentColor"
        d={
          icon === "facebook"
            ? "M14 8h3V4h-3c-2.8 0-4.5 1.8-4.5 4.6V11H7v4h2.5v9h4v-9h3l.5-4h-3.5V8.9c0-.6.4-.9 1-.9z"
            : "M16.5 3c.4 2.3 1.9 3.9 4.2 4.1v3.3c-1.6 0-3-.5-4.2-1.3v6.3c0 3.4-2.7 5.8-5.9 5.6-2.9-.2-5.2-2.6-5.1-5.5.1-3.2 2.9-5.6 6.1-5.2v3.4c-1.3-.4-2.8.4-2.9 1.9-.1 1.2.8 2.2 2 2.3 1.3.1 2.3-.9 2.3-2.2V3h3.5z"
        }
      />
    </svg>
  );
};

/* The north-east arrow beside a linked office name; it nudges out on hover. */
const ArrowIcon = () => (
  <svg
    viewBox="0 0 16 16"
    aria-hidden="true"
    focusable="false"
    className="h-3 w-3 shrink-0 opacity-70 transition group-hover:-translate-y-px group-hover:translate-x-px group-hover:opacity-100"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 11 11 5M6 5h5v5" />
  </svg>
);

/*
  One office: its name, then its icons straight after it rather than pushed to the far
  edge, so the two read as one thing. An account with no site of its own (Europe, SE Asia)
  shows its name as plain text, with no arrow, since there is nowhere for it to go.
*/
const OfficeRow = ({ office }: { office: OfficeLink }) => (
  <li className="flex min-h-[1.75rem] flex-wrap items-center gap-x-3 gap-y-1.5">
    {office.site ? (
      <a href={office.site.href} className={OFFICE_NAME_LINK}>
        {office.name}
        <ArrowIcon />
      </a>
    ) : (
      <span className="font-medium text-[#ffffff]/[0.72]">{office.name}</span>
    )}
    {office.socials.length > 0 && (
      <span className="flex items-center gap-1.5">
        {office.socials.map(({ network, href }) => (
          <a
            key={network}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`PCG ${office.name} on ${SOCIAL_LABEL[network]}`}
            className={SOCIAL_BUTTON}
          >
            <SocialIcon icon={network} />
          </a>
        ))}
      </span>
    )}
  </li>
);

const OFFICE_LINKS = officeLinks();

const POLICIES = [
  {
    href: "https://premiercardgrading.com/policies/privacy-policy",
    text: "Privacy Policy",
  },
  {
    href: "https://premiercardgrading.com/policies/terms-of-service",
    text: "Terms of Service",
  },
];

/*
  "Your nearest PCG" (D20-D22, Robert, 2026-10-20). It replaced the one "Visit
  premiercardgrading.com" button, which sent every visitor to the US shop.

  The offices the browser's time zone suggests come first, as gold buttons; under them, a
  select of all eight. A visitor whose zone suggests nothing sees the select alone. Either
  way the choice is remembered (lib/offices saveOffice) before the browser leaves, so the
  next visit's Submit cards goes to the same office (D23).

  The select navigates as soon as an office is picked, as D22 asks. Its first option is a
  placeholder that cannot be picked, so the select never starts on an office.
*/
const NearestOffice = () => {
  const { suggested } = useOfficeChoice();

  const onSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const office = officeByHref(event.target.value);
    if (office) {
      chooseOffice(office);
      window.location.assign(office.href);
    }
  };

  return (
    <div className="md:w-80 md:shrink-0 xl:w-auto">
      <p id="footer-nearest-label" className={FOOTER_HEADING}>
        Your nearest PCG
      </p>
      {suggested.length > 0 && (
        <ul aria-labelledby="footer-nearest-label" className="mb-6 flex flex-wrap gap-2">
          {suggested.map((office) => (
            <li key={office.href}>
              <a
                href={office.href}
                onClick={() => chooseOffice(office)}
                className={FOOTER_GOLD_BUTTON}
              >
                PCG {office.text}
                <span aria-hidden="true">→</span>
              </a>
            </li>
          ))}
        </ul>
      )}
      <label
        htmlFor="footer-office-select"
        className="mb-2 block text-xs text-[#ffffff]/[0.72]"
      >
        All PCG offices
      </label>
      <select
        id="footer-office-select"
        defaultValue=""
        onChange={onSelect}
        className={FOOTER_SELECT}
      >
        <option value="" disabled className={FOOTER_OPTION}>
          Choose an office…
        </option>
        {GRADING_SITES.map((office) => (
          <option key={office.href} value={office.href} className={FOOTER_OPTION}>
            {office.text}
          </option>
        ))}
      </select>
    </div>
  );
};

const Footer = () => {
  const container = PAGE_CONTAINER;

  return (
    <footer className="relative w-full bg-[#0B0E14] text-[#ffffff]">
      {/* A gold hairline along the top, fading out at both ends. */}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#DEBB68] to-transparent"
      />

      <div className={`${container} grid gap-12 py-16 lg:grid-cols-12 lg:gap-x-8`}>
        {/*
          The brand column, spread out (Robert, 2026-10-21). From md to lg it has the full row,
          so the mark and blurb sit on the left and the nearest PCG on the right instead of one
          narrow stack with a dead half beside it; on xl it is a column again, with room
          between its parts.
        */}
        <div className="flex flex-col gap-10 md:flex-row md:justify-between lg:col-span-12 xl:col-span-4 xl:flex-col xl:justify-start">
          <div className="max-w-sm">
            <img src={LogoImage} alt="Premier Card Grading" className="h-12 w-auto" />
            <p className="mt-5 text-sm leading-relaxed text-[#ffffff]/[0.72]">
              The PCG Population Report: every card Premier Card Grading has certified, across
              every PCG office, updated live.
            </p>
          </div>
          <NearestOffice />
        </div>

        {/*
          Headings are <p>s, not h2s: the footer is not a section of the document outline,
          and an h2 down here would read as a level the content does not have. Each list
          points at its heading instead, so a screen reader announces the group by name.
        */}
        <nav aria-labelledby="footer-offices-label" className="lg:col-span-9 xl:col-span-6">
          <p id="footer-offices-label" className={`mb-2 ${FOOTER_HEADING_TEXT}`}>
            Visit or follow our offices
          </p>
          <p id="footer-offices-hint" className="mb-5 text-xs text-[#ffffff]/[0.55]">
            Tap an office to visit its site, or an icon to follow it.
          </p>
          <ul
            aria-labelledby="footer-offices-label"
            aria-describedby="footer-offices-hint"
            className="grid gap-x-10 gap-y-3 text-sm sm:grid-cols-2"
          >
            {OFFICE_LINKS.map((office) => (
              <OfficeRow key={office.name} office={office} />
            ))}
          </ul>
        </nav>

        <nav aria-labelledby="footer-policies-label" className="lg:col-span-3 xl:col-span-2">
          <p id="footer-policies-label" className={FOOTER_HEADING}>
            Policies
          </p>
          <ul className="flex flex-col gap-2.5 text-sm">
            {POLICIES.map((policy) => (
              <li key={policy.href}>
                <a href={policy.href} className={FOOTER_LINK}>
                  {policy.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <div className="border-t border-[#ffffff]/[0.08]">
        <div
          className={`${container} flex flex-col gap-2 py-5 text-xs text-[#ffffff]/[0.55] sm:flex-row sm:items-center sm:justify-between`}
        >
          <p>© {new Date().getFullYear()} Premier Card Grading. All rights reserved.</p>
          <p>Population figures update live from PCG grading records.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
